import { useState } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TOKEN_PRICE_HINT, tokenPriceText, type Assistant, type ProviderKind, type ProviderOption } from "../../api";
import { useProviderOptions, useUpdateAssistant } from "../../hooks";
import { Field } from "./controls";

type Props = {
  kind: ProviderKind;
  assistant: Assistant;
  current: ProviderOption | null;
  patchKey: "transcriber_id" | "llm_id" | "voice_id";
  /** Label of the second select, e.g. "Model" or "Voice". */
  optionLabel: string;
};

/**
 * Provider + option pickers fed by the admin-managed catalog. Picking an
 * option saves it on the assistant immediately (prices/latency stay in the
 * catalog — the assistant only references the row).
 */
export default function ProviderPicker({ kind, assistant, current, patchKey, optionLabel }: Props) {
  const { options, isLoading } = useProviderOptions(kind);
  const updateMut = useUpdateAssistant(assistant.id);
  const [provider, setProvider] = useState(current?.provider ?? "");

  // value = internal provider id (shared with the pipeline), label = display
  const providers = [...new Map(options.map((o) => [o.provider, o.provider_label])).entries()];
  const effectiveProvider = provider || current?.provider || providers[0]?.[0] || "";
  const choices = options.filter((o) => o.provider === effectiveProvider);
  // "" keeps the Select controlled while showing its placeholder.
  const selectedId =
    current && current.provider === effectiveProvider ? current.id : "";

  if (!isLoading && options.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        No {kind} options are configured yet. A site admin can add them (with
        pricing and latency) in the provider catalog.
      </p>
    );
  }

  return (
    <>
      <Field label="Provider">
        <Select value={effectiveProvider} onValueChange={setProvider} disabled={isLoading}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder={isLoading ? "Loading…" : "Select provider"} />
          </SelectTrigger>
          <SelectContent>
            {providers.map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label={optionLabel}>
        <Select
          value={selectedId}
          onValueChange={(id) => updateMut.mutate({ [patchKey]: id })}
          disabled={isLoading || updateMut.isPending}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder={`Select ${optionLabel.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {choices.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name} · {o.latency_ms}ms{kind === "model" ? ` · ${modelSpeed(o.latency_ms)}` : ""}
                {tokenPriceText(o) ? ` · ${tokenPriceText(o)}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {choices.some((o) => tokenPriceText(o)) && (
          <p className="mt-1 text-xs text-muted-foreground">{TOKEN_PRICE_HINT}</p>
        )}
      </Field>
    </>
  );
}

/** How quickly a model starts replying on a call, from its catalog latency. */
export function modelSpeed(ms: number): string {
  if (ms <= 300) return "Fastest";
  if (ms <= 550) return "Fast";
  return "Slower";
}
